/**
 * The plumbing every report shares: one period, one loader, one place a failure lands.
 *
 * The period is the reason this file exists. It is the thing somebody touches immediately
 * before reading every number on the screen, so it is held by the area and handed to each
 * report — a control that resets when you change report or view is a control nobody trusts,
 * and a screen read against the wrong dates is worse than no screen at all.
 *
 * Two rules travel with it, and both are easy to get subtly wrong:
 *
 * - **Last week and last month mean the previous complete week and month.** A rolling seven
 *   days is a number nobody invoices from, and a firm settling up against a figure that
 *   keeps moving will settle against the wrong one. The server decides; this side sends a
 *   preset name.
 * - **Every period is resolved in the firm's timezone, on the server.** Each answer carries
 *   the dates it was computed over, and those are what the screen echoes back — a laptop set
 *   to whichever timezone somebody chose when they installed Windows cannot quietly report a
 *   different month from the one it claims.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { describe } from '../settings/errors';
import { defaultPeriod, reportsApi, type DutySettings, type Period } from './api';
import { periodFrom } from './back';

/** The dates a report was actually computed over. Every report payload is one of these. */
export interface Resolved {
  from: string;
  to: string;
}

/**
 * What the area holds and every report page is given.
 *
 * A page never owns the period, and never renders the resolved line or an error itself: it
 * hands this object to `PeriodRow` and to `useReport`, and those two do it once.
 */
export interface ReportFrame {
  /** The period every report is read over. Survives a change of report and of view. */
  period: Period;
  /** Choose another period. `PeriodRow` is the only thing that should call it. */
  setPeriod: (next: Period) => void;
  /** True while the report on screen is being fetched, and while it is being replaced. */
  busy: boolean;
  /** The dates the report on screen was computed over — null while `busy`, so the line on
   *  screen can never belong to the report that was there a moment ago. */
  resolved: Resolved | null;
  /** The last dates the server resolved, kept across a change of report. The custom range
   *  seeds from these rather than from browser-UTC today, which is a different day in the
   *  firm's calendar often enough to matter. */
  seed: Resolved | null;
  /** How a load publishes what it is doing. Stable for the life of one report. */
  publish: (state: { busy: boolean; resolved: Resolved | null }) => void;
  /** How a failure reaches the one banner above the page. Stable for the life of one report. */
  fail: (problem: string | null) => void;
}

/** Every report page takes exactly this, and nothing else. */
export interface ReportPageProps {
  frame: ReportFrame;
}

/** What `useReport` gives a page back. */
export interface ReportLoad<T> {
  /** The last answer. Kept on screen while the next one is fetched. */
  data: T | null;
  /** True while this particular fetch is in flight. */
  busy: boolean;
  /** Fetch again — after a payment is recorded or reversed, say. */
  reload: () => void;
}

/**
 * The area's own state, in one hook.
 *
 * `publish` and `fail` are bound to the report currently routed to, so an answer that
 * arrives after somebody has moved on cannot label the new screen with the old one's dates.
 */
export function useFrame(report: string): { frame: ReportFrame; problem: string | null } {
  const [params] = useSearchParams();

  // A link back to a report carries the period it was left on (#478), read once on arrival.
  const [period, setPeriod] = useState<Period>(() => periodFrom(params) ?? defaultPeriod('lastThreeMonths'));
  const [seed, setSeed] = useState<Resolved | null>(null);

  const [load, setLoad] = useState<{ report: string; busy: boolean; resolved: Resolved | null }>({
    report,
    busy: true,
    resolved: null,
  });

  const [fault, setFault] = useState<{ report: string; problem: string | null }>({
    report,
    problem: null,
  });

  const publish = useCallback(
    (next: { busy: boolean; resolved: Resolved | null }) => {
      setLoad({ report, ...next });

      // The seed is deliberately not scoped to the report: somebody who read last month's
      // shift book and then asks the demand report for a custom range means those dates.
      if (next.resolved !== null) setSeed(next.resolved);
    },
    [report],
  );

  const fail = useCallback(
    (problem: string | null) => setFault({ report, problem }),
    [report],
  );

  const frame = useMemo<ReportFrame>(
    () => ({
      period,
      setPeriod,
      // A state published by the report that was on screen before this one says nothing
      // about this one, so it reads as busy rather than as somebody else's dates.
      busy: load.report === report ? load.busy : true,
      resolved: load.report === report ? load.resolved : null,
      seed,
      publish,
      fail,
    }),
    [period, load, report, seed, publish, fail],
  );

  return { frame, problem: fault.report === report ? fault.problem : null };
}

/**
 * One fetch, over the frame's period.
 *
 * Pass `null` for `load` when this is not the thing on screen — the fleet table while the
 * grid is showing — and the hook holds its last answer without fetching or publishing.
 * Fetching all of them because a page has three would be three queries over three months of
 * a whole fleet for the one somebody actually wanted.
 *
 * The previous answer stays in `data` while the next is in flight, so changing the period
 * does not blank the screen and then fill it again.
 *
 * @param key Anything else the fetch depends on: a driver id, a view. A string rather than
 *   a dependency array, so that a caller cannot quietly break the exhaustive-deps rule.
 */
export function useReport<T extends Resolved>(
  frame: ReportFrame,
  load: ((period: Period) => Promise<T>) | null,
  key = '',
): ReportLoad<T> {
  const { period, publish, fail } = frame;
  const { t } = useTranslation();

  const [data, setData] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The loader is a fresh closure on every render — over a driver id, a view — and putting
  // it in the effect's dependencies would refetch on every render forever. It is read
  // through a ref that this effect, declared first, refreshes before the fetch below runs.
  const latest = useRef(load);

  useEffect(() => {
    latest.current = load;
  });

  const active = load !== null;

  useEffect(() => {
    if (!active) return;

    let cancelled = false;

    // Fetching is the external system this effect synchronises with, and saying so before
    // the request goes out is the whole point: it is what clears the resolved line, so the
    // dates on screen can never belong to the answer that is being replaced.
    // eslint-disable-next-line react/set-state-in-effect
    setBusy(true);
    publish({ busy: true, resolved: null });
    fail(null);

    void (async () => {
      try {
        const fetch = latest.current;

        if (fetch === null) return;

        const answer = await fetch(period);

        if (cancelled) return;

        setData(answer);
        publish({ busy: false, resolved: { from: answer.from, to: answer.to } });
      } catch (error) {
        if (cancelled) return;

        fail(describe(error, t, 'settings.couldNotLoad'));
        publish({ busy: false, resolved: null });
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, key, period, nonce, publish, fail, t]);

  const reload = useCallback(() => setNonce((count) => count + 1), []);

  return { data, busy, reload };
}

/**
 * The firm's duty settings, for the one report that needs them.
 *
 * The demand report's heatmap starts its week where the firm starts its week; the grid gets
 * its threshold in the answer itself and does not need this.
 */
export function useDutySettings(): DutySettings | null {
  const [settings, setSettings] = useState<DutySettings | null>(null);

  useEffect(() => {
    let cancelled = false;

    void reportsApi
      .duty()
      .then((answer) => {
        if (!cancelled) setSettings(answer);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  return settings;
}
